import { apiFetch, fetchWithAuthRetry } from '../../api/client'

export type PulseEvent = {
  id: string
  occurred_at: string
  display_date: string | null
  display_label: string | null
  type: string
  title: string
  actor: string
  description: string
  amount_cents: number | null
  record: {
    kind?: string
    id?: number | null
    href?: string | null
    pay_app_id?: number | null
    change_order_id?: number | null
    replaced_id?: number | null
  }
}

export async function fetchPulseTimeline(
  projectId: number,
  opts: { q?: string; types?: string; order?: string; since?: string } = {},
): Promise<{ events: PulseEvent[]; timezone: string }> {
  const qs = new URLSearchParams()
  if (opts.q) qs.set('q', opts.q)
  if (opts.types) qs.set('types', opts.types)
  if (opts.order) qs.set('order', opts.order)
  if (opts.since) qs.set('since', opts.since)
  const suffix = qs.toString() ? `?${qs}` : ''
  const packed = await apiFetch<{ data: { events: PulseEvent[]; timezone: string } }>(
    'GET',
    `/api/projects/${projectId}/pulse/timeline${suffix}`,
  )
  return packed.data
}

export type PulsePaymentRow = {
  id: number
  source: 'manual' | 'stripe' | string
  amount: string | number
  method: string | null
  paid_at: string | null
  pay_app_id: number
  app_number: number | null
  due_date: string | null
  submitted_at?: string | null
  actor?: string | null
}

export type PulsePaymentsPayload = {
  payments: PulsePaymentRow[]
  typical: { owner: string | null; n_paid: number; median_days: number | null }
  collected: number
  payment_terms?: string | null
  terms_days?: number | null
}

export type PulseHandover = {
  accepted_by: string
  accepted_at: string
  actor_id?: number
  recorded_at?: string
  evidence_original_name?: string | null
}

export async function fetchPulsePayments(projectId: number): Promise<PulsePaymentsPayload> {
  const packed = await apiFetch<{ data: PulsePaymentsPayload }>(
    'GET',
    `/api/projects/${projectId}/pulse/payments`,
  )
  return packed.data
}

export async function fetchPulseCloseout(projectId: number): Promise<{ handover: PulseHandover | null }> {
  const packed = await apiFetch<{ data: { handover: PulseHandover | null } }>(
    'GET',
    `/api/projects/${projectId}/pulse/closeout`,
  )
  return packed.data
}

export async function uploadPulseFile(
  projectId: number,
  file: File,
  opts: { docType: string; notes?: string } = { docType: 'other' },
): Promise<unknown> {
  const body = new FormData()
  body.append('file', file)
  body.append('doc_type', opts.docType)
  if (opts.notes) body.append('notes', opts.notes)
  const res = await fetchWithAuthRetry(`/api/projects/${projectId}/hub/uploads`, {
    method: 'POST',
    body,
  })
  const packed = await res.json() as { data?: unknown; error?: string; message?: string }
  if (!res.ok) throw new Error(packed.message || packed.error || `HTTP ${res.status}`)
  return packed.data
}

export async function uploadWarrantyDocument(projectId: number, file: File): Promise<unknown> {
  return uploadPulseFile(projectId, file, { docType: 'warranty' })
}

export async function recordPulseHandover(
  projectId: number,
  input: { accepted_by: string; accepted_at: string; file?: File | null },
): Promise<PulseHandover> {
  const body = new FormData()
  body.append('accepted_by', input.accepted_by)
  body.append('accepted_at', input.accepted_at)
  if (input.file) body.append('file', input.file)
  const res = await fetchWithAuthRetry(`/api/projects/${projectId}/pulse/closeout/handover`, {
    method: 'POST',
    body,
  })
  const packed = await res.json() as { data?: PulseHandover; error?: string; message?: string }
  if (!res.ok) throw new Error(packed.message || packed.error || `HTTP ${res.status}`)
  return packed.data as PulseHandover
}

export async function fetchPulseFileBlob(path: string): Promise<Blob> {
  const res = await fetchWithAuthRetry(path, { method: 'GET' })
  const type = res.headers.get('content-type') || ''
  if (!res.ok || type.includes('application/json')) {
    throw new Error('unavailable')
  }
  return res.blob()
}

export async function uploadChangeOrderAttachment(
  projectId: number,
  changeOrderId: number,
  file: File,
): Promise<unknown> {
  const body = new FormData()
  body.append('file', file)
  const res = await fetchWithAuthRetry(`/api/projects/${projectId}/change-orders/${changeOrderId}/attachments`, {
    method: 'POST',
    body,
  })
  const packed = await res.json() as { data?: unknown; error?: string; message?: string }
  if (!res.ok) throw new Error(packed.message || packed.error || `HTTP ${res.status}`)
  return packed.data
}
